import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { IStrapiEntity } from '@core/interfaces/strapi.interface';
import { TStrapiTag } from '@core/types/strapi.type';
import { ISimple } from '@noda-lib/utils/interfaces';
import { ArticlesBannerImports } from '@pages/articles/banner/articles-banner.imports';
import { ArticlesDataService } from '@pages/articles/services/articles.data.service';
import { LanguageService } from '@translate/services/language.service';

import { IArticle } from '../interfaces/article.interface';

@Component({
  selector: 'noda-articles-banner',
  standalone: true,
  templateUrl: './articles-banner.component.html',
  styleUrls: ['./articles-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ArticlesBannerImports,
})
export class ArticlesBannerComponent {
  private articlesService: ArticlesDataService = inject(ArticlesDataService);
  private langService: LanguageService = inject(LanguageService);

  public article: InputSignal<IArticle> = input<IArticle>();
  public needLink: InputSignal<boolean> = input<boolean>(false);
  protected resolvedArticle: Signal<IArticle> = computed(() => {
    return this.article() || this.articlesService.articleHero();
  });

  protected badgeClasses: Signal<ISimple<boolean>> = computed(() => ({
    'bg-dark': !!this.resolvedArticle().preview.background.image.data?.attributes.url,
    neutral: this.resolvedArticle().preview.colorTextIsBlack,
  }));

  public get imageUrl(): string {
    return this.resolvedArticle().preview.background.image.data?.attributes.url;
  }

  public get iconUrl(): string {
    return this.resolvedArticle().preview.background.icon.data?.attributes.url;
  }

  public get colorBlack(): boolean {
    return this.resolvedArticle().preview.colorTextIsBlack;
  }

  public get backgroundColor(): string {
    return this.resolvedArticle().preview.background.color;
  }

  public get badges(): IStrapiEntity<TStrapiTag>[] {
    return this.resolvedArticle().preview.tags.data;
  }

  public get articleUrl(): string[] | string {
    switch (true) {
      case this.needLink() && this.isLanguageMatching:
        return this.resolvedArticle().path;

      case this.needLink() && !this.isLanguageMatching:
        return `/articles/${this.resolvedArticle().path}`;

      default:
        return null;
    }
  }

  private get isLanguageMatching(): boolean {
    return this.resolvedArticle().language?.data?.attributes.value === this.langService.activeLang;
  }
}
